/**
 * Command-side session hygiene for multi-account / multi-org browsers.
 * Keys must stay in sync with webapp/src/lib/sessionHygiene.ts + api.ts.
 */

export const SAAS_TOKEN_KEY = "og_saas_hub_token";
export const SAAS_HUB_URL_KEY = "og_saas_hub_url";
/** Branded hub for agent MCP snippets (not Command origin / not necessarily browser API base). */
export const SAAS_MCP_HUB_URL_KEY = "og_saas_mcp_hub_url";
export const SAAS_ORG_SLUG_KEY = "og_saas_org_slug";
/** Clerk org id for sticky hub restore across refresh. */
export const SAAS_ORG_ID_KEY = "og_saas_org_id";
export const OSS_TOKEN_KEY = "og_token";
export const ONBOARD_DISMISSED_KEY = "og_onboard_dismissed";
export const CHECKOUT_INTENT_KEY = "og_checkout_intent";

function safeSessionRemove(key: string) {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* private mode */
  }
}

function safeLocalRemove(key: string) {
  try {
    localStorage.removeItem(key);
  } catch {
    /* private mode */
  }
}

/** Wipe Command SaaS hub credentials from sessionStorage. */
export function clearSaasCredentials(): void {
  safeSessionRemove(SAAS_TOKEN_KEY);
  safeSessionRemove(SAAS_HUB_URL_KEY);
  safeSessionRemove(SAAS_MCP_HUB_URL_KEY);
  safeSessionRemove(SAAS_ORG_SLUG_KEY);
  safeSessionRemove(SAAS_ORG_ID_KEY);
  // Same-origin Live Ops iframe reads sessionStorage — tell it to drop React
  // auth state so Revoke/list keys don't 401 with a ghost "connected" UI.
  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("og-saas-credentials-cleared"));
      // Also postMessage for the iframe (CustomEvent does not cross documents)
      const frames = document.querySelectorAll("iframe");
      for (const f of frames) {
        try {
          (f as HTMLIFrameElement).contentWindow?.postMessage(
            { type: "og-saas-credentials-cleared" },
            window.location.origin
          );
        } catch {
          /* ok */
        }
      }
    }
  } catch {
    /* ok */
  }
}

/** Drop durable OSS/local token copy (SaaS must not leave keys here). */
export function clearOssToken(): void {
  safeLocalRemove(OSS_TOKEN_KEY);
}

/** Clear Live Ops identity keys that bleed across accounts. */
export function clearIdentityLocal(): void {
  safeLocalRemove("og_name");
  safeLocalRemove("og_role");
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith("og_pid_")) keys.push(k);
    }
    for (const k of keys) localStorage.removeItem(k);
  } catch {
    /* ok */
  }
}

/** Org switch: drop hub credentials so iframe cannot re-apply the previous org. */
export function clearSessionForOrgSwitch(): void {
  clearSaasCredentials();
  clearOssToken();
  clearIdentityLocal();
}

/**
 * Clerk sign-out / account switch: full wipe of SaaS + local auth crumbs.
 * Keeps theme preference.
 */
export function clearSessionForSignOut(): void {
  clearSaasCredentials();
  clearOssToken();
  clearIdentityLocal();
  safeSessionRemove(ONBOARD_DISMISSED_KEY);
  safeSessionRemove(CHECKOUT_INTENT_KEY);
}

/** Notify Live Ops iframe to wipe its storage + state. */
export function postSignOutToOps(iframe: HTMLIFrameElement | null): void {
  try {
    iframe?.contentWindow?.postMessage(
      { type: "og-saas-signout" },
      window.location.origin
    );
  } catch {
    /* ignore */
  }
}
